<script setup>
import { computed } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

const { content, contentAttributes } = useMessageContext();
const { t } = useI18n();

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const readKey = (object, camelKey, snakeKey = null) => {
  if (!object) return undefined;
  return firstPresent(
    object[camelKey],
    snakeKey ? object[snakeKey] : undefined
  );
};

const formatCurrency = (amount, currency = 'BRL') => {
  if (amount === undefined || amount === null || amount === '') return '';

  if (typeof amount === 'string' && Number.isNaN(Number(amount))) {
    return amount;
  }

  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(Number(amount));
  } catch {
    return `${currency || 'BRL'} ${Number(amount).toFixed(2)}`;
  }
};

const normalizeProduct = product => ({
  name: readKey(product, 'name'),
  retailerId: readKey(product, 'retailerId', 'retailer_id'),
  price: readKey(product, 'price'),
  imageUrl: readKey(product, 'imageUrl', 'image_url'),
  currency: readKey(product, 'currency'),
  url: firstPresent(
    readKey(product, 'url'),
    readKey(product, 'productUrl', 'product_url'),
    readKey(product, 'permalink')
  ),
});

const normalizeOrderItem = item => ({
  productRetailerId: readKey(item, 'productRetailerId', 'product_retailer_id'),
  quantity: Number(readKey(item, 'quantity')) || 1,
  itemPrice: readKey(item, 'itemPrice', 'item_price'),
  currency: readKey(item, 'currency') || 'BRL',
});

const attributes = computed(() => contentAttributes.value || {});

const productInfo = computed(
  () => attributes.value.productInfo || attributes.value.product_info || {}
);

const orderInfo = computed(
  () => attributes.value.orderInfo || attributes.value.order_info || {}
);

const interactiveInfo = computed(() => attributes.value.interactive || {});

// The AI's send_catalog tool writes catalog_info; the manual send flow writes
// an interactive payload. Same WhatsApp message, two shapes on the way in.
const rawCatalogInfo = computed(
  () => attributes.value.catalogInfo || attributes.value.catalog_info
);

const catalogInfo = computed(() => rawCatalogInfo.value || {});

const isProductMessage = computed(
  () =>
    productInfo.value.type === 'product' ||
    productInfo.value.type === 'product_list'
);

const isCatalogMessage = computed(
  () =>
    interactiveInfo.value.type === 'catalog_message' ||
    productInfo.value.type === 'catalog_message' ||
    !!rawCatalogInfo.value
);

const isOrderMessage = computed(
  () => !!readKey(orderInfo.value, 'catalogId', 'catalog_id')
);

const products = computed(() =>
  (productInfo.value.products || []).map(normalizeProduct)
);

const firstProduct = computed(() => products.value[0] || {});

const isSingleProduct = computed(() => productInfo.value.type === 'product');

const catalogBody = computed(() =>
  firstPresent(
    interactiveInfo.value.body?.text,
    catalogInfo.value.body,
    productInfo.value.body,
    content.value
  )
);

const orderProductsByRetailerId = computed(() => {
  const orderProducts = Array.isArray(orderInfo.value.products)
    ? orderInfo.value.products.map(normalizeProduct)
    : [];

  return orderProducts.reduce((memo, product) => {
    if (product.retailerId) memo[product.retailerId] = product;
    return memo;
  }, {});
});

const orderItems = computed(() =>
  (readKey(orderInfo.value, 'productItems', 'product_items') || [])
    .map(normalizeOrderItem)
    .map(item => ({
      ...item,
      product: orderProductsByRetailerId.value[item.productRetailerId] || {},
    }))
);

const orderItemCount = computed(() =>
  orderItems.value.reduce((sum, item) => sum + item.quantity, 0)
);

const orderItemCountLabel = computed(() => {
  if (orderItemCount.value === 1) {
    return t('CATALOG_PRODUCTS.ITEM_COUNT_ONE', {
      count: orderItemCount.value,
    });
  }

  return t('CATALOG_PRODUCTS.ITEM_COUNT_OTHER', {
    count: orderItemCount.value,
  });
});

const orderTotal = computed(() => {
  const total = readKey(orderInfo.value, 'total');
  const totalAmount = readKey(total, 'amount');
  if (totalAmount !== undefined) {
    return {
      amount: totalAmount,
      currency: readKey(total, 'currency') || 'BRL',
    };
  }

  const pricedItems = orderItems.value.filter(
    item => item.itemPrice !== undefined && item.itemPrice !== null
  );
  if (!pricedItems.length) return null;

  return {
    amount: pricedItems.reduce(
      (sum, item) => sum + Number(item.itemPrice) * item.quantity,
      0
    ),
    currency: pricedItems[0].currency || 'BRL',
  };
});

const orderTotalLabel = computed(() => {
  if (!orderTotal.value) return '';
  return t('CATALOG_PRODUCTS.ESTIMATED_TOTAL', {
    total: formatCurrency(orderTotal.value.amount, orderTotal.value.currency),
  });
});

const orderPreviewProduct = computed(
  () => orderItems.value.find(item => item.product?.imageUrl)?.product || {}
);

const productUrl = computed(() => firstProduct.value.url || null);
</script>

<template>
  <BaseBubble class="p-0 overflow-hidden" data-bubble-name="whatsapp-commerce">
    <div
      v-if="isOrderMessage"
      class="w-[17rem] max-w-[75vw] overflow-hidden"
      data-test-id="whatsapp-catalog-order-card"
    >
      <div class="px-3 pt-3 pb-2">
        <p class="text-xs font-medium text-n-slate-11">
          {{ t('CATALOG_PRODUCTS.ORDER_VIA_CATALOG') }}
        </p>
        <div class="flex gap-3 mt-2 p-2 rounded-lg bg-n-alpha-2">
          <div
            class="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-white dark:bg-n-solid-2 flex items-center justify-center"
          >
            <img
              v-if="orderPreviewProduct.imageUrl"
              :src="orderPreviewProduct.imageUrl"
              :alt="orderPreviewProduct.name || ''"
              class="w-full h-full object-cover"
            />
            <span v-else class="i-lucide-shopping-bag size-7 text-n-slate-10" />
          </div>
          <div class="flex-1 min-w-0 self-center">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-shopping-cart size-4 text-n-slate-11" />
              <p class="text-sm font-semibold text-n-slate-12">
                {{ orderItemCountLabel }}
              </p>
            </div>
            <p
              v-if="orderTotalLabel"
              class="text-sm text-n-slate-11 mt-0.5 truncate"
            >
              {{ orderTotalLabel }}
            </p>
          </div>
        </div>
        <div v-if="orderItems.length" class="mt-2 space-y-1">
          <div
            v-for="item in orderItems"
            :key="item.productRetailerId"
            class="flex justify-between gap-3 text-xs text-n-slate-11"
          >
            <span class="truncate">
              {{ item.product.name || item.productRetailerId }}
            </span>
            <span class="shrink-0">
              {{
                t('CATALOG_PRODUCTS.QUANTITY_MULTIPLIER', {
                  count: item.quantity,
                })
              }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="border-t border-n-weak px-3 py-2.5 text-center text-sm font-medium text-primary-500"
      >
        {{ t('CATALOG_PRODUCTS.VIEW_DETAILS') }}
      </div>
    </div>

    <div
      v-else-if="isProductMessage"
      class="w-[17rem] max-w-[75vw] overflow-hidden"
      data-test-id="whatsapp-product-card"
    >
      <template v-if="isSingleProduct && firstProduct.name">
        <div
          class="w-full aspect-square bg-white dark:bg-n-solid-2 flex items-center justify-center overflow-hidden"
        >
          <img
            v-if="firstProduct.imageUrl"
            :src="firstProduct.imageUrl"
            :alt="firstProduct.name"
            class="w-full h-full object-cover"
          />
          <span v-else class="i-lucide-package size-10 text-n-slate-10" />
        </div>
        <div class="px-3 py-2.5">
          <p class="text-base font-semibold text-n-slate-12 leading-snug">
            {{ firstProduct.name }}
          </p>
          <p v-if="firstProduct.price" class="text-sm text-n-slate-11 mt-1">
            {{ firstProduct.price }}
          </p>
        </div>
        <a
          v-if="productUrl"
          :href="productUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="block border-t border-n-weak px-3 py-2.5 text-center text-sm font-medium text-primary-500"
        >
          {{ t('CATALOG_PRODUCTS.VIEW') }}
        </a>
        <div
          v-else
          class="border-t border-n-weak px-3 py-2.5 text-center text-sm font-medium text-primary-500"
        >
          {{ t('CATALOG_PRODUCTS.VIEW') }}
        </div>
      </template>

      <div v-else-if="products.length > 1" class="px-3 py-3">
        <p
          v-if="productInfo.header"
          class="text-sm font-semibold text-n-slate-12 mb-2"
        >
          {{ productInfo.header }}
        </p>
        <p v-if="productInfo.body" class="text-sm text-n-slate-11 mb-2">
          {{ productInfo.body }}
        </p>
        <div class="space-y-2">
          <div
            v-for="product in products"
            :key="product.retailerId"
            class="flex gap-2 items-center"
          >
            <div
              class="shrink-0 w-9 h-9 rounded-md overflow-hidden bg-n-alpha-2 flex items-center justify-center"
            >
              <img
                v-if="product.imageUrl"
                :src="product.imageUrl"
                :alt="product.name || ''"
                class="w-full h-full object-cover"
              />
              <span v-else class="i-lucide-package size-4 text-n-slate-10" />
            </div>
            <span class="text-sm text-n-slate-12 truncate">
              {{ product.name || product.retailerId }}
            </span>
            <span
              v-if="product.price"
              class="text-xs text-n-slate-11 shrink-0 ml-auto"
            >
              {{ product.price }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="flex gap-2 items-center">
        <span class="i-lucide-shopping-bag size-5 text-n-slate-10" />
        <span class="text-sm text-n-slate-12">{{ content }}</span>
      </div>
    </div>

    <div
      v-else-if="isCatalogMessage"
      class="w-[17rem] max-w-[75vw] overflow-hidden"
      data-test-id="whatsapp-catalog-message-card"
    >
      <div class="px-3 py-3">
        <div
          class="w-full aspect-[1.6] rounded-lg bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-store size-10 text-n-slate-10" />
        </div>
        <p v-if="catalogBody" class="text-sm text-n-slate-12 mt-3">
          {{ catalogBody }}
        </p>
      </div>
      <div
        class="border-t border-n-weak px-3 py-2.5 text-center text-sm font-medium text-primary-500"
      >
        {{ t('CATALOG_PRODUCTS.VIEW_CATALOG') }}
      </div>
    </div>

    <div v-else class="flex gap-2 items-center px-3 py-2">
      <span class="i-lucide-shopping-bag size-5 text-n-slate-10" />
      <span class="text-sm text-n-slate-12">{{ content }}</span>
    </div>
  </BaseBubble>
</template>
